<template>
  <Card variant="cockpit" rounded class="overflow-hidden" no-space>
    <Flex justify-between items-center class="h-10 border-b border-cockpit-line">
      <IconInput icon="fa-chevron-left" class="border-r border-cockpit-line" @click="prevMonth()" />
      <span v-html="monthName" />
      <IconInput icon="fa-chevron-right" class="border-x border-cockpit-line" @click="nextMonth()" />
    </Flex>
    <div class="grid grid-cols-7 border-b border-cockpit-line bg-cockpit-elevated py-2 text-xs text-cockpit-muted">
      <div class="text-center font-bold">DOM</div>
      <div class="text-center font-bold">SEG</div>
      <div class="text-center font-bold">TER</div>
      <div class="text-center font-bold">QUA</div>
      <div class="text-center font-bold">QUI</div>
      <div class="text-center font-bold">SEX</div>
      <div class="text-center font-bold">SAB</div>
    </div>
    <div class="grid grid-cols-7 text-xs">
      <Flex
        center
        v-for="(day, i) in calendar"
        :key="i"
        class="relative h-10 cursor-pointer border-b border-r border-cockpit-line transition-colors"
        :class="[hoverClasses, contextualClasses(day)]"
        @click="selectDate(day)">
        <span>{{ day.getDate() }}</span>
        <span v-if="hasEvent(day)" class="absolute bottom-1 h-1 w-1 rounded-full bg-cockpit-warning"></span>
      </Flex>
    </div>
  </Card>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useCalendar } from '@/composables/useCalendar'

const props = defineProps({
  modelValue: [String, Array], // Armazena a data selecionada
  range: Boolean,
  eventDates: {type: Array, default: () => []},
})

const emits = defineEmits(['update:modelValue'])
const { calendar, targetDate, mountMonth, prevMonth, nextMonth } = useCalendar()

// Inicializa o mês atual
const date = new Date()
mountMonth(date)

const rangedDate = ref(props.modelValue ?? [])
const hoverClasses = ['hover:bg-cockpit-green/20', 'hover:border-cockpit-green', 'hover:text-cockpit-text']

const dayKey = (day) => {
  const year = day.getFullYear()
  const month = String(day.getMonth() + 1).padStart(2, '0')
  const date = String(day.getDate()).padStart(2, '0')
  return `${year}-${month}-${date}`
}

const hasEvent = (day) => props.eventDates.includes(dayKey(day))
const dateFromValue = (value) => {
  if (!value) return null
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const selectDate = (day) => {
  if (props.range) {
    if (rangedDate.value.length < 2) {
      rangedDate.value.push(dayKey(day))
    } else {
      rangedDate.value = []
      rangedDate.value.push(dayKey(day))
    }
    emits('update:modelValue', rangedDate.value)
  } else {
    const formattedDate = dayKey(day)
    emits('update:modelValue', formattedDate)
  }
}

const inDateRange = (rawDay) => {
  if (!props.range) {
    return false
  }

  const day = dayKey(rawDay)
  const start = rangedDate.value[0] ? dayKey(dateFromValue(rangedDate.value[0])) : ''
  const end = rangedDate.value[1] ? dayKey(dateFromValue(rangedDate.value[1])) : ''
  if (start && end && day >= start && day <= end) {
    return true
  } else if (start && day === start) {
    return true
  }

  return false
}

// Aplica classe para a data selecionada
const contextualClasses = (day) => {
  const today = new Date()
  const todayStr = dayKey(today)
  const dayStr = dayKey(day)
  const isToday = dayStr === todayStr

  const selectedValues = Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]
  const isSelected = selectedValues.some((value) => {
    const selectedDate = dateFromValue(value)
    return selectedDate && dayKey(selectedDate) === dayStr
  })
  const isOutsideMonth = day.getMonth() !== targetDate.value.getMonth()

  if (isSelected) {
    return 'bg-cockpit-green/20 text-cockpit-green' // Data selecionada
  }
  if (isToday) {
    return 'bg-cockpit-green/10 text-cockpit-green' // Dia de hoje
  }
  if (isOutsideMonth) {
    return 'bg-cockpit-elevated text-cockpit-muted' // Fora do mês atual
  }

  if (inDateRange(day)) {
    return 'bg-cockpit-green/10 text-cockpit-green' // Fora do mês atual
  }
  return ''
}

// Nome do mês no cabeçalho
const monthName = computed(() => {
  if (!targetDate.value) return ''
  const month = targetDate.value.toLocaleString('default', { month: 'long' })
  const year = targetDate.value.toLocaleString('default', { year: 'numeric' })
  return `<span class="capitalize">${month}</span> de ${year}`
})
</script>
